import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FirebaseService } from '../services/firebaseService';

interface AdminEclairagePublicSubcategoriesProps {
  t: any;
  isRTL: boolean;
  formData: any;
  onInputChange: (key: string, value: any) => void;
}

const Container = styled.div<{ $isRTL: boolean }>`
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const SectionTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1.5rem 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const FormGroup = styled.div<{ $isRTL: boolean }>`
  margin-bottom: 1.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const Label = styled.label<{ $isRTL: boolean }>`
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const Input = styled.input<{ $isRTL: boolean }>`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:focus {
    outline: none;
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
`;

const TextArea = styled.textarea<{ $isRTL: boolean }>`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:focus {
    outline: none;
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
`;

const Button = styled.button<{ $isRTL: boolean; $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  ${props => props.$variant === 'primary' ? `
    background: #dc2626;
    color: white;
    
    &:hover {
      background: #b91c1c;
    }
  ` : `
    background: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #e5e7eb;
    }
  `}
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SubcategoriesList = styled.div`
  margin-top: 2rem;
`;

const SubcategoryCard = styled.div<{ $isRTL: boolean }>`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const SubcategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const SubcategoryTitle = styled.h4<{ $isRTL: boolean }>`
  font-size: 1.2rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const SubcategoryActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button<{ $variant: 'edit' | 'delete' }>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.$variant === 'edit' ? `
    background: #3b82f6;
    color: white;
    
    &:hover {
      background: #2563eb;
    }
  ` : `
    background: #ef4444;
    color: white;
    
    &:hover {
      background: #dc2626;
    }
  `}
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  font-size: 1.1rem;
  color: #64748b;
`;

interface Subcategory {
  id: string;
  name: string;
  description: string;
  image: string;
  createdAt: Date;
  updatedAt: Date;
}

const AdminEclairagePublicSubcategories: React.FC<AdminEclairagePublicSubcategoriesProps> = ({ t, isRTL, formData, onInputChange }) => {
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState<Subcategory | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadSubcategories();
  }, []);

  const loadSubcategories = async () => {
    try {
      setLoading(true);
      const data = await FirebaseService.getAll('eclairage-public-subcategories');
      setSubcategories(data);
    } catch (error) {
      console.error('Error loading subcategories:', error);
    } finally {
      setLoading(false);
    }

  };
  const handleFileUpload = async (file: File, field: string) => {
    if (!file) return;

    const subcategory = (formData.newSubcategory || {}) || {};
    setUploading(prev => ({ ...prev, [field]: true }));

    try {
      const url = await FirebaseService.uploadFile(`eclairage-public-subcategories/${field}/${Date.now()}_${file.name}`, file);
      onInputChange('newSubcategory', { ...subcategory, [field]: url });
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Erreur lors du téléchargement du fichier');
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleAddSubcategory = () => {
    setEditingSubcategory(null);
    setShowForm(true);
    onInputChange('newSubcategory', {
      name: '',
      description: '',
      image: ''
    });
  };

  const handleEditSubcategory = (subcategory: Subcategory) => {
    setEditingSubcategory(subcategory);
    setShowForm(true);
    onInputChange('newSubcategory', subcategory);
  };

  const handleDeleteSubcategory = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette sous-catégorie ?')) {
      try {
        await FirebaseService.delete('eclairage-public-subcategories', id);
        loadSubcategories();
      } catch (error) {
        console.error('Error deleting subcategory:', error);
      }
    }
  };

  const handleSaveSubcategory = async () => {
    try {
      const subcategoryData = (formData.newSubcategory || {});
      
      if (editingSubcategory) {
        await FirebaseService.update('eclairage-public-subcategories', editingSubcategory.id, subcategoryData);
      } else {
        await FirebaseService.create('eclairage-public-subcategories', subcategoryData);
      }
      
      setShowForm(false);
      setEditingSubcategory(null);
      loadSubcategories();
    } catch (error) {
      console.error('Error saving subcategory:', error);
    }
  };

  if (loading) {
    return <LoadingSpinner>Chargement des sous-catégories...</LoadingSpinner>;
  }

  return (
    <Container $isRTL={isRTL}>
      <SectionTitle $isRTL={isRTL}>Gestion des Sous-catégories Éclairage Public</SectionTitle>
      
      <ButtonGroup>
        <Button $isRTL={isRTL} $variant="primary" onClick={handleAddSubcategory}>
          Ajouter une sous-catégorie
        </Button>
      </ButtonGroup>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}

          animate={{ opacity: 1, y: 0 }}

          style={{ marginTop: '2rem', background: '#f8fafc', padding: '2rem', borderRadius: '12px' }}

        >
          <SectionTitle $isRTL={isRTL}>
            {editingSubcategory ? 'Modifier la sous-catégorie' : 'Nouvelle sous-catégorie'}
          </SectionTitle>
          
          <FormGroup $isRTL={isRTL}>
            <Label $isRTL={isRTL}>Nom de la sous-catégorie</Label>
            <Input
              $isRTL={isRTL}
              type="text"

              value={(formData.newSubcategory || {})?.name || ''}
              onChange={(e) => onInputChange('newSubcategory', { ...(formData.newSubcategory || {}), name: e.target.value })}

            />
          </FormGroup>

          <FormGroup $isRTL={isRTL}>
            <Label $isRTL={isRTL}>Description</Label>
            <TextArea
              $isRTL={isRTL}
              value={(formData.newSubcategory || {})?.description || ''}
              onChange={(e) => onInputChange('newSubcategory', { ...(formData.newSubcategory || {}), description: e.target.value })}
            />
          </FormGroup>

          <FormGroup $isRTL={isRTL}>
            <Label $isRTL={isRTL}>Image</Label>
            <Input
              $isRTL={isRTL}
              type="file" accept="image/*"

              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file, 'image');
              }}

            />
          </FormGroup>

          <ButtonGroup>
            <Button $isRTL={isRTL} $variant="primary" onClick={handleSaveSubcategory}>
              {editingSubcategory ? 'Mettre à jour' : 'Créer'}
            </Button>
            <Button $isRTL={isRTL} $variant="secondary" onClick={() => setShowForm(false)}>
              Annuler
            </Button>
          </ButtonGroup>
        </motion.div>
      )}

      <SubcategoriesList>
        {subcategories.map((subcategory) => (
          <SubcategoryCard key={subcategory.id} $isRTL={isRTL}>
            <SubcategoryHeader>
              <SubcategoryTitle $isRTL={isRTL}>{subcategory.name}</SubcategoryTitle>
              <SubcategoryActions>
                <ActionButton $variant="edit" onClick={() => handleEditSubcategory(subcategory)}>
                  Modifier
                </ActionButton>
                <ActionButton $variant="delete" onClick={() => handleDeleteSubcategory(subcategory.id)}>
                  Supprimer
                </ActionButton>
              </SubcategoryActions>
            </SubcategoryHeader>
            <p>{subcategory.description}</p>
          </SubcategoryCard>
        ))}
      </SubcategoriesList>
    </Container>
  );
};

export default AdminEclairagePublicSubcategories;
